import React from "react";
import { FiArrowLeft } from "react-icons/fi";
import type { Chat } from "../../../../redux/reducers/ChatReducer/ChatSliceTypes";
import type { User } from "../../../../redux/reducers/UserSlice/UserSliceType";
import PropertyCard from "./PropertyCard";
import BookingDates from "./BookingDates";
import ActionButtons from "./ActionButtons";
import { UI_TEXT } from "../../constants/messages.constants";
import { defaultPropertyImage } from "../../../../utils/defaultImages";

interface DetailsPanelProps {
  selectedChat: Chat | null;
  currentUser: User | null;
  bookingId: number | null;
  bookingQueryId: number | null;
  booking: any;
  bookingQuery: any;
  onProceedToBooking: () => void;
  onProceedToPayment: () => void;
  onClose?: () => void;
  isMobile?: boolean;
}

/**
 * DetailsPanel Component
 * Right panel containing property details, booking info, and actions
 */
const DetailsPanel: React.FC<DetailsPanelProps> = ({
  selectedChat,
  currentUser,
  bookingId,
  bookingQueryId,
  booking,
  bookingQuery,
  onProceedToBooking,
  onProceedToPayment,
  onClose,
  isMobile = false,
}) => {
  if (!selectedChat) return null;

  const content = (
    <div className="p-6">
      {/* Property Details */}
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4">
          {bookingId
            ? UI_TEXT.SECTIONS.RESERVATION_DETAILS
            : bookingQueryId
            ? UI_TEXT.SECTIONS.BOOKING_QUERY
            : UI_TEXT.SECTIONS.PROPERTY_DETAILS}
        </h3>

        {selectedChat.property && (
          <PropertyCard
            property={selectedChat.property}
            defaultImage={defaultPropertyImage}
          />
        )}
      </div>

      {/* Action Buttons */}
      <ActionButtons
        bookingId={bookingId}
        bookingQueryId={bookingQueryId}
        currentUser={currentUser}
        onProceedToBooking={onProceedToBooking}
        onProceedToPayment={onProceedToPayment}
      />

      {/* Check-in/Check-out Dates */}
      {(booking || bookingQuery) && (
        <BookingDates
          checkInDate={
            booking?.check_in_date || bookingQuery?.check_in_date || ""
          }
          checkOutDate={
            booking?.check_out_date || bookingQuery?.check_out_date || ""
          }
        />
      )}
    </div>
  );

  // Mobile: Full screen overlay
  if (isMobile) {
    return (
      <div className="fixed inset-0 z-50 bg-white dark:bg-neutral-800 overflow-y-auto">
        <div className="p-4">
          <div className="flex justify-between items-center mb-6">
            <button
              onClick={onClose}
              className="p-2 text-neutral-900 dark:text-white hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-lg transition-all"
              aria-label="Close details"
            >
              <FiArrowLeft size={20} />
            </button>
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-white">
              {UI_TEXT.SECTIONS.DETAILS}
            </h3>
            <div className="w-8"></div>
          </div>
          {content}
        </div>
      </div>
    );
  }

  // Desktop: Side panel
  return (
    <div className="hidden md:block w-1/3 lg:w-1/4 border-l border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 overflow-y-auto">
      {content}
    </div>
  );
};

export default DetailsPanel;
